/**
 * Default settings for the UTM Linker plugin
 */

import { optionSchemaDefaults } from './defaultSettingsData.js';

/**
 * Default settings object.
 *
 * The OPTION-SCHEMA fields (everything includes/Rest/OptionsMapper.php's
 * CAMEL_TO_SNAKE map knows about — debugMode, mediumEnabled, mediumValue,
 * utmContentRules, excludeClasses/Domains, messengerRewrite,
 * normalizeValues, shortenerSkip/Domains, existingUtm,
 * clickIdPassthrough(+Domains)/clickIdStrictConsent, leadBeacon(+Attribution))
 * are spread in from optionSchemaDefaults — generated from
 * config/default-settings.json by build/write-default-settings.mjs, the
 * SAME data includes/Admin/Settings.php's `$defaults` and
 * src/admin/store/defaults.js's `defaultOptions` derive from. Production
 * always passes an explicit, PHP-computed value for every one of these
 * (Admin/Settings.php $defaults, localized via Frontend/Scripts.php) — these
 * are purely the fallback for a missing/pre-load value, never observed on a
 * real WordPress site.
 *
 * `i18n`/`consentGranted`/`consentApiPresent` below are JS-only runtime
 * concerns with no stored-option equivalent, so they stay hand-written here
 * rather than living in the JSON SSOT.
 */
export const defaultSettings = {
    ...optionSchemaDefaults,

    // Consent signal resolved server-side (Frontend/Scripts.php) via the WP
    // Consent API (wp_has_consent('marketing')), or the admin's own opt-in
    // when no CMP is present. Defaults to false here purely as a fail-safe
    // for a missing/stale value — production always passes an explicit
    // boolean computed by PHP. JS-only runtime concern, not a stored option
    // (absent from optionSchemaDefaults/config/default-settings.json).
    consentGranted: false,

    // Whether a WP Consent API-compatible CMP is present on the site
    // (informational; reserved for a future runtime consent-change listener).
    // Same JS-only-concern caveat as consentGranted above.
    consentApiPresent: false,

    // Internationalization strings
    i18n: {
        debugTitle: 'UTM Linker Debug',
        hideButton: 'Hide',
        showButton: 'Show',
        hostname: 'Hostname',
        path: 'Path',
        utmMedium: 'UTM Medium',
        utmCampaign: 'UTM Campaign',
        processedLinks: 'Processed Links',
        imageLink: 'Image link',
        link: 'Link',
        alreadyHasUtm: 'Already has UTM parameters',
        error: {
            invalidUrl: 'UTM Linker: Invalid URL format'
        }
    }
};

/**
 * Parse an input that might be a string or array into an array
 * 
 * @param {Array|string} input The input to parse
 * @return {Array} The parsed array
 */
export function parseArray(input) {
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') {
        return input.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
}

/**
 * Parse an input that might be a string or object into an object
 * 
 * @param {Object|string} input The input to parse
 * @return {Object} The parsed object
 */
export function parseObject(input) {
    if (input && typeof input === 'object' && !Array.isArray(input)) return input;
    if (typeof input === 'string') {
        try {
            return JSON.parse(input);
        } catch (e) {
            return {};
        }
    }
    return {};
}

/**
 * Parse an input that might already be an array, or a JSON-encoded string,
 * into an array. Sibling of parseObject() above (same tolerant-JSON.parse
 * contract, generalized for an array shape) — pr-review-council LOW #10:
 * previously leadBeaconAttribution hand-rolled its own inline
 * array-or-JSON-string branch here instead of sharing this helper.
 * Deliberately distinct from parseArray() above: parseArray() treats a
 * STRING as a comma/newline-separated list of scalar values (e.g.
 * excludeDomains); this is for fields whose array holds STRUCTURED entries
 * (objects), where a string input is JSON, never a comma list.
 *
 * @param {Array|string} input The input to parse
 * @return {Array} The parsed array (empty on any failure/wrong shape)
 */
export function parseArrayOfObjects(input) {
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') {
        try {
            const parsed = JSON.parse(input);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }
    return [];
}

/**
 * True for a plain object (not an array, not null) — the shape mergeConfigObject()
 * below recurses into; anything else (array, string, number, boolean) is
 * treated as a leaf value that replaces wholesale.
 *
 * @param {any} value
 * @return {boolean}
 */
function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Deep-merge a nested config object (utmContentRules) key-wise over its
 * defaults: an OBJECT-valued key at any depth merges key-by-key, so an
 * override that only mentions ONE sibling doesn't silently drop the others.
 * An ARRAY-valued key (e.g. utmContentRules.button_classes) still REPLACES
 * wholesale — merging arrays element-wise would make removing an entry
 * impossible for the admin to express.
 *
 * @param {Object} defaultsObj The default config object.
 * @param {Object} overridesObj The caller-provided override (already parsed
 *   via parseObject() upstream, so this only ever sees a real object).
 * @return {Object} A new merged object; neither input is mutated.
 */
function mergeConfigObject(defaultsObj, overridesObj) {
    const result = { ...defaultsObj };

    for (const key of Object.keys(overridesObj)) {
        const defaultValue = defaultsObj[key];
        const overrideValue = overridesObj[key];

        result[key] = isPlainObject(defaultValue) && isPlainObject(overrideValue)
            ? mergeConfigObject(defaultValue, overrideValue)
            : overrideValue;
    }

    return result;
}

/**
 * Merge user settings with default settings
 *
 * @param {Object} userSettings User-provided settings
 * @return {Object} Merged settings
 */
export function mergeSettings(userSettings = {}) {
    const settings = { ...defaultSettings };

    // Process the utm_medium toggle + its single value (v1.9.0 medium-toggle
    // refactor — REPLACES the removed pathRules/domRules page-type
    // classification subsystem). An empty/whitespace-only mediumValue falls
    // back to the default rather than persisting an empty utm_medium (same
    // rule Admin/Settings.php's sanitize enforces server-side).
    if (typeof userSettings.mediumEnabled !== 'undefined') {
        settings.mediumEnabled = Boolean(userSettings.mediumEnabled);
    }

    if (typeof userSettings.mediumValue === 'string' && userSettings.mediumValue.trim()) {
        settings.mediumValue = userSettings.mediumValue;
    }

    // List-item container CSS selector (v1.10.0, positionContainerSelector) —
    // drives rank-based utm_term numbering (UtmLinker.js/PositionResolver.js).
    // Unlike mediumValue above, an EMPTY string is itself a meaningful,
    // deliberate value (turns the feature off, restoring legacy per-link
    // monotonic numbering) — accept any string as-is, including '', rather
    // than falling back to the default on a falsy/empty check.
    if (typeof userSettings.positionContainerSelector === 'string') {
        settings.positionContainerSelector = userSettings.positionContainerSelector;
    }

    // Process utm content rules (deep-merged, same rationale as pathRules)
    if (userSettings.utmContentRules) {
        settings.utmContentRules = mergeConfigObject(settings.utmContentRules, parseObject(userSettings.utmContentRules));
    }
    
    // Process exclude classes
    if (userSettings.excludeClasses) {
        settings.excludeClasses = parseArray(userSettings.excludeClasses);
    }
    
    // Process exclude domains
    if (userSettings.excludeDomains) {
        settings.excludeDomains = parseArray(userSettings.excludeDomains);
    }
    
    // Process debug mode
    if (typeof userSettings.debugMode !== 'undefined') {
        settings.debugMode = Boolean(userSettings.debugMode);
    }

    // Process messenger-rewrite opt-in flag
    if (typeof userSettings.messengerRewrite !== 'undefined') {
        settings.messengerRewrite = Boolean(userSettings.messengerRewrite);
    }

    // Process value-normalization opt-out flag
    if (typeof userSettings.normalizeValues !== 'undefined') {
        settings.normalizeValues = Boolean(userSettings.normalizeValues);
    }

    // Process shortener-skip opt-out flag + its additional-domains list
    if (typeof userSettings.shortenerSkip !== 'undefined') {
        settings.shortenerSkip = Boolean(userSettings.shortenerSkip);
    }

    if (userSettings.shortenerDomains) {
        settings.shortenerDomains = parseArray(userSettings.shortenerDomains);
    }

    // Process already-has-UTM mode — an invalid/unrecognized value falls
    // back to 'skip' (the safe, original behavior), never silently trusting
    // an unvalidated string through to the engine's own gates.
    if (typeof userSettings.existingUtm !== 'undefined') {
        settings.existingUtm = ['skip', 'override', 'merge'].includes(userSettings.existingUtm)
            ? userSettings.existingUtm
            : 'skip';
    }

    // Process click-id passthrough opt-in flag + its domain allowlist
    if (typeof userSettings.clickIdPassthrough !== 'undefined') {
        settings.clickIdPassthrough = Boolean(userSettings.clickIdPassthrough);
    }

    if (userSettings.clickIdPassthroughDomains) {
        settings.clickIdPassthroughDomains = parseArray(userSettings.clickIdPassthroughDomains);
    }

    // Process the resolved consent signal (+ CMP-presence flag)
    if (typeof userSettings.consentGranted !== 'undefined') {
        settings.consentGranted = Boolean(userSettings.consentGranted);
    }

    if (typeof userSettings.consentApiPresent !== 'undefined') {
        settings.consentApiPresent = Boolean(userSettings.consentApiPresent);
    }

    // Process Lead Beacon opt-in flag + its attribution map. The map is
    // real structured data (array of {ad_id, hosts, tels} objects), not a
    // comma/newline string list like clickIdPassthroughDomains — accept a
    // real array as-is, or a JSON-encoded string, via parseArrayOfObjects()
    // (parseObject()'s sibling, above).
    if (typeof userSettings.leadBeacon !== 'undefined') {
        settings.leadBeacon = Boolean(userSettings.leadBeacon);
    }

    if (userSettings.leadBeaconAttribution) {
        settings.leadBeaconAttribution = parseArrayOfObjects(userSettings.leadBeaconAttribution);
    }

    // Process i18n
    if (userSettings.i18n) {
        settings.i18n = {
            ...settings.i18n,
            ...userSettings.i18n
        };
    }

    // Carry the admin flag through — it gates the debug overlay to logged-in admins.
    if (typeof userSettings.isAdmin !== 'undefined') {
        settings.isAdmin = Boolean(userSettings.isAdmin);
    }

    return settings;
}